<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IPaymentSystemsFields } from '@modules/payment-systems/types';
import type { IWithdrawalsHistoryFormFields } from '@modules/withdrawals-history/submodules/view/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { useFormatDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { usePaymentSystems } from '@modules/payment-systems/composables';
import {
  ADMIN_WITHDRAWALS_HISTORY_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_WITHDRAWALS_HISTORY,
} from '@modules/withdrawals-history/constants';
import { useWithdrawalsHistoryItemQuery } from '@modules/withdrawals-history/submodules/view/queries';
import { isString } from '@utils/guards';

const route = useRoute();
const { getTranslation } = useI18n();

const id = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const { data: withdrawal, isLoading } = useWithdrawalsHistoryItemQuery(id);
const { formatDate } = useFormatDate();

const paymentSystem = computed(() => withdrawal.value?.payment_system_name);
const values = computed<IPaymentSystemsFields>(() => withdrawal.value?.payment_system_details_json ?? {});

const {
  paymentSystemSchema,
} = usePaymentSystems(
  paymentSystem,
  undefined,
  ref(false),
  ref('default'),
  true,
  values,
);

const formSchema = computed<FormSchema<IWithdrawalsHistoryFormFields, never, {
  country_code: {
    selectOptionValue: 'country_code';
    selectOptionLabel: 'name';
  };
}>>(() => ({
  merchant_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_MERCHANT_NAME').value,
    value: withdrawal.value?.merchant_name,
  },
  affiliate_email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_AFFILIATE_EMAIL').value,
    value: withdrawal.value?.affiliate_email,
  },
  affiliate_id: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_AFFILIATE_ID').value,
    value: withdrawal.value?.affiliate_id,
    useGrouping: false,
    maxFractionDigits: 0,
  },
  affiliate_username: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_AFFILIATE_USERNAME').value,
    value: withdrawal.value?.affiliate_username,
  },
  amount: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_AMOUNT').value,
    value: withdrawal.value?.amount,
  },
  created_at: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_CREATED_AT').value,
    value: withdrawal.value?.created_at ? formatDate(withdrawal.value.created_at) : undefined,
  },
  creator_type: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_CREATOR_TYPE').value,
    value: withdrawal.value?.creator_type,
  },
  creator_id: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_CREATOR_ID').value,
    value: withdrawal.value?.creator_id,
    useGrouping: false,
    maxFractionDigits: 0,
  },
  creator_email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_CREATOR_EMAIL').value,
    value: withdrawal.value?.creator_email,
  },
  admin_email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_ADMIN_EMAIL').value,
    value: withdrawal.value?.admin_email,
  },
  state: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_STATE').value,
    value: withdrawal.value?.state ? getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, `STATE_${withdrawal.value.state.toUpperCase()}`).value : undefined,
  },
  payment_system_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'FORM_PAYMENT_SYSTEM_NAME').value,
    value: withdrawal.value?.payment_system_name,
  },
  ...paymentSystemSchema.value,
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'WithdrawalsHistoryViewPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'VIEW_HEADER')"
  >
    <VLoader
      :loading="isLoading"
      min-height="200px"
    >
      <VForm
        :schema="formSchema"
        readonly
      >
        <template #secondaryBtn>
          <VBackButton
            :to="{
              name: ADMIN_WITHDRAWALS_HISTORY_LIST_ROUTE_NAME,
            }"
            :label="$getTranslation(TRANSLATION_GROUP_WITHDRAWALS_HISTORY, 'VIEW_BACK')"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
